import React, { useState, useEffect } from 'react';
import {
  Star,
  MessageSquare,
  AlertCircle,
  CheckCircle,
  ExternalLink,
  TrendingUp,
  Bell
} from 'lucide-react';
import apiService from './services/api';
import { Panel, Section, StatCard, Badge, LoadingSpinner } from './components';

function QualityPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [googleReviews, setGoogleReviews] = useState([]);
  const [googleStats, setGoogleStats] = useState(null);
  const [reviewAutomationEnabled, setReviewAutomationEnabled] = useState(false);
  const [ratingFilter, setRatingFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [boostLocationId, setBoostLocationId] = useState('');
  const [locations, setLocations] = useState([]);
  const [savingBoost, setSavingBoost] = useState(false);
  const [reviewPlatform, setReviewPlatform] = useState('google');
  const [savingPlatform, setSavingPlatform] = useState(false);
  const [reviewRequests, setReviewRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('reviews');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [reviewsResponse, statsResponse, settingsResponse, requestsResponse, locationsResponse, boostResponse, platformResponse] = await Promise.all([
        apiService.getGoogleReviews({ ordering: '-review_date' }),
        apiService.getGoogleReviewStats(),
        apiService.getSettings({ name: 'review_automation', parameter: 'enabled' }),
        apiService.getReviewRequests(),
        apiService.getLocations(),
        apiService.getSettings({ name: 'review_automation', parameter: 'boost_location_id' }),
        apiService.getSettings({ name: 'review_automation', parameter: 'review_platform' }),
      ]);

      setGoogleReviews(reviewsResponse.results || []);
      setGoogleStats(statsResponse);
      setReviewRequests(requestsResponse.results || requestsResponse || []);
      setLocations(locationsResponse.results || locationsResponse || []);

      const enabledSetting = settingsResponse.results?.find(
        s => s.name === 'review_automation' && s.parameter === 'enabled'
      );
      setReviewAutomationEnabled(enabledSetting?.value === 'true');

      const boostSetting = boostResponse.results?.find(
        s => s.name === 'review_automation' && s.parameter === 'boost_location_id'
      );
      setBoostLocationId(boostSetting?.value || '');

      const platformSetting = platformResponse.results?.find(
        s => s.name === 'review_automation' && s.parameter === 'review_platform'
      );
      setReviewPlatform(platformSetting?.value || 'google');

    } catch (err) {
      console.error('Error loading quality data:', err);
      setError('Failed to load quality data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const renderStars = (rating, size = 'w-4 h-4') => {
    return (
      <div className="flex items-center space-x-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${size} ${star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  const reviewLocations = [...new Set(googleReviews.map(r => r.location_name).filter(Boolean))];

  const filteredReviews = googleReviews.filter(review => {
    if (locationFilter !== 'all' && review.location_name !== locationFilter) return false;
    if (ratingFilter === 'all') return true;
    if (ratingFilter === 'positive') return review.rating >= 4;
    if (ratingFilter === 'negative') return review.rating <= 2;
    if (ratingFilter === 'unreplied') return !review.reply;
    return review.rating === parseInt(ratingFilter);
  });

  if (loading) {
    return (
      <div className="flex-1 bg-neutral-50 p-3 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 bg-neutral-50 p-3">
        <Panel className="p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">{error}</p>
          <button onClick={loadData} className="mt-4 px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900">
            Retry
          </button>
        </Panel>
      </div>
    );
  }

  const hasReviews = googleStats && googleStats.total_reviews > 0;

  return (
    <div className="flex-1 bg-neutral-50 p-3 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      {/* Page Title */}
      <div className="flex items-center justify-between mb-3">
        <h1 className="text-lg font-semibold text-gray-900">QUALITY</h1>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">Send to:</span>
            <select
              value={reviewPlatform}
              onChange={async (e) => {
                const val = e.target.value;
                setSavingPlatform(true);
                try {
                  await apiService.post('/amn/settings/upsert/', {
                    name: 'review_automation',
                    parameter: 'review_platform',
                    value: val,
                  });
                  setReviewPlatform(val);
                } catch (err) {
                  console.error('Failed to save platform setting:', err);
                } finally {
                  setSavingPlatform(false);
                }
              }}
              disabled={savingPlatform}
              className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-red-800"
            >
              <option value="google">Google</option>
              <option value="yelp">Yelp</option>
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">Boost to:</span>
            <select
              value={boostLocationId}
              onChange={async (e) => {
                const val = e.target.value;
                setSavingBoost(true);
                try {
                  await apiService.post('/amn/settings/upsert/', {
                    name: 'review_automation',
                    parameter: 'boost_location_id',
                    value: val,
                  });
                  setBoostLocationId(val);
                } catch (err) {
                  console.error('Failed to save boost setting:', err);
                } finally {
                  setSavingBoost(false);
                }
              }}
              disabled={savingBoost}
              className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-red-800"
            >
              <option value="">Auto (per location)</option>
              {locations.map(loc => (
                <option key={loc.id} value={String(loc.id)}>{loc.name}</option>
              ))}
            </select>
          </div>
          <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${reviewAutomationEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
            <Bell className="w-3 h-3" />
            <span>Review requests: {reviewAutomationEnabled ? 'ON' : 'OFF'}</span>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {hasReviews ? (
        <Section className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
          <StatCard
            title="Average Rating"
            value={googleStats.average_rating?.toFixed(1) || '—'}
            icon={Star}
            iconColor="text-yellow-500"
          />
          <StatCard
            title="Total Reviews"
            value={googleStats.total_reviews || 0}
            icon={MessageSquare}
            iconColor="text-blue-600"
          />
          <StatCard
            title="5-Star Reviews"
            value={googleStats.by_rating?.[5] || 0}
            icon={TrendingUp}
            iconColor="text-green-600"
          />
          <StatCard
            title="Needs Reply"
            value={googleStats.unreplied_count || 0}
            icon={AlertCircle}
            iconColor={googleStats.unreplied_count > 0 ? 'text-orange-500' : 'text-gray-400'}
          />
        </Section>
      ) : (
        <Panel className="mb-3 p-6 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
              <Star className="w-8 h-8 text-gray-400" />
            </div>
          </div>
          <h2 className="text-lg font-semibold text-gray-800 mb-2">No Google Reviews Yet</h2>
          <p className="text-sm text-gray-500 mb-4 max-w-md mx-auto">
            Quality tracking is based on real client feedback from Google reviews.
            Reviews will appear here once imported.
          </p>
          <div className="flex justify-center space-x-3">
            <a
              href="https://business.google.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              View on Google Business
            </a>
          </div>
        </Panel>
      )}

      {/* Rating Distribution */}
      {hasReviews && (
        <Panel className="mb-3 p-4">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Rating Distribution</h3>
          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map((rating) => {
              const count = googleStats.by_rating?.[rating] || 0;
              const percentage = googleStats.total_reviews > 0
                ? (count / googleStats.total_reviews * 100)
                : 0;
              return (
                <div key={rating} className="flex items-center space-x-3">
                  <div className="flex items-center space-x-1 w-16">
                    <span className="text-sm text-gray-600">{rating}</span>
                    <Star className="w-3 h-3 text-yellow-400 fill-current" />
                  </div>
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${rating >= 4 ? 'bg-green-500' : rating === 3 ? 'bg-yellow-500' : 'bg-red-500'}`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-500 w-8 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </Panel>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-3">
        <button
          onClick={() => setActiveTab('reviews')}
          className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors ${activeTab === 'reviews' ? 'border-red-800 text-red-800' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Google Reviews ({googleStats?.total_reviews || 0})
        </button>
        <button
          onClick={() => setActiveTab('requests')}
          className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors ${activeTab === 'requests' ? 'border-red-800 text-red-800' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Review Requests ({reviewRequests.length})
        </button>
      </div>

      {/* Review Requests Tab */}
      {activeTab === 'requests' && (
        <Panel className="p-4">
          {reviewRequests.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MessageSquare className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No review requests sent yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-1.5 px-2 text-gray-500 font-medium">Client</th>
                    <th className="text-left py-1.5 px-2 text-gray-500 font-medium">Team Member</th>
                    <th className="text-left py-1.5 px-2 text-gray-500 font-medium">Location</th>
                    <th className="text-left py-1.5 px-2 text-gray-500 font-medium">Sent</th>
                    <th className="text-center py-1.5 px-2 text-gray-500 font-medium">Rating</th>
                    <th className="text-left py-1.5 px-2 text-gray-500 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reviewRequests.map((req) => (
                    <tr key={req.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-1.5 px-2 text-gray-900 font-medium">{req.client_name || req.phone_number}</td>
                      <td className="py-1.5 px-2 text-gray-600">{req.technician_name || '—'}</td>
                      <td className="py-1.5 px-2 text-gray-600">{req.location_name || '—'}</td>
                      <td className="py-1.5 px-2 text-gray-600">
                        {req.initial_sms_sent_at ? new Date(req.initial_sms_sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                      <td className="py-1.5 px-2 text-center">
                        {req.client_rating ? (
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded font-medium ${req.client_rating === 5 ? 'bg-green-100 text-green-800' : req.client_rating >= 4 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                            {req.client_rating}/5
                          </span>
                        ) : '—'}
                      </td>
                      <td className="py-1.5 px-2">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs ${
                          req.status === 'review_link_sent' ? 'bg-green-100 text-green-700' :
                          req.status === 'completed' ? 'bg-gray-100 text-gray-600' :
                          req.status === 'pending_rating' ? 'bg-yellow-100 text-yellow-700' :
                          req.status === 'rating_received' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {req.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}

      {/* Google Reviews Tab */}
      {activeTab === 'reviews' && hasReviews && (
        <Panel className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-800">Reviews</h3>
            <div className="flex items-center gap-2">
            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="px-2 py-1 text-xs bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
            >
              <option value="all">All Locations</option>
              {reviewLocations.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
            <select
              value={ratingFilter}
              onChange={(e) => setRatingFilter(e.target.value)}
              className="px-2 py-1 text-xs bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
            >
              <option value="all">All Reviews</option>
              <option value="positive">Positive (4-5★)</option>
              <option value="negative">Negative (1-2★)</option>
              <option value="unreplied">Needs Reply</option>
              <option value="5">5 Stars</option>
              <option value="4">4 Stars</option>
              <option value="3">3 Stars</option>
              <option value="2">2 Stars</option>
              <option value="1">1 Star</option>
            </select>
            </div>
          </div>

          {filteredReviews.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MessageSquare className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No reviews match this filter</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredReviews.map((review) => (
                <div key={review.id} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-gray-900">{review.reviewer_name}</span>
                        {renderStars(review.rating, 'w-3 h-3')}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-xs text-gray-500">{formatDate(review.review_date)}</p>
                        {review.location_name && (
                          <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">{review.location_name}</span>
                        )}
                      </div>
                    </div>
                    {!review.reply && (
                      <Badge variant="warning">Needs reply</Badge>
                    )}
                    {review.reply && (
                      <Badge variant="success">Replied</Badge>
                    )}
                  </div>
                  {review.comment && (
                    <p className="text-sm text-gray-700 mt-3">{review.comment}</p>
                  )}
                  {review.reply && (
                    <div className="mt-3 pl-4 border-l-2 border-red-200 bg-white rounded p-3">
                      <p className="text-xs text-gray-500 mb-1">Your response:</p>
                      <p className="text-sm text-gray-700">{review.reply}</p>
                      {review.reply_date && (
                        <p className="text-xs text-gray-400 mt-1">{formatDate(review.reply_date)}</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      {/* Info Panel when no reviews */}
      {!hasReviews && (
        <Panel className="p-4">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">How Quality Tracking Works</h3>
          <div className="space-y-3 text-sm text-gray-600">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-red-800">1</span>
              </div>
              <p>Google reviews from clients are imported and displayed here</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-red-800">2</span>
              </div>
              <p>Track your average rating, review volume, and response rate</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-red-800">3</span>
              </div>
              <p>
                {reviewAutomationEnabled
                  ? 'Review request SMS is enabled — clients receive a link after appointments'
                  : 'Enable review request SMS to automatically ask clients for feedback after appointments'
                }
              </p>
            </div>
          </div>
        </Panel>
      )}
    </div>
  );
}

export default QualityPage;
